/* eslint-disable max-statements */
import axios from 'axios';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars-2';
import {
	QueryClient,
	QueryClientProvider,
	useMutation,
	useQuery
} from 'react-query';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../../../config/axiosInstance';
import { globalEnv } from '../../../config/baseConfig';
import { useTypedSelector } from '../../../hooks/useTypeSelector';
import { SetNotificationAction } from '../../../redux/actions/notificationAction';
import { hideSearchbarAction } from '../../../redux/actions/searchBarAction';
import {
	setSelectedSection,
	setTab
} from '../../../redux/actions/sideBarAction';
import LazyImage from '../../Utils/Components/lazyImageComponent';
import Editable from '../../Utils/editable';
import { DataNotFoundAnimation } from '../../Utils/ErrorAnimations/dataNotFoundAnim';
import LoadingAnimComponent from '../../Utils/LoadingAnimations/loadingAnimation';
import { stateNotificationsEnum } from '../../Utils/notification';
import { TopicCard } from './singleTopic';
import TopicAddModalForDash from './topicAddModal';

const queryClient = new QueryClient();

export default function UserDashboard() {
	const dispatch = useDispatch();
	dispatch(setSelectedSection('Dashboard Details'));
	dispatch(setTab('documents'));
	dispatch(hideSearchbarAction());
	return (
		<QueryClientProvider client={queryClient}>
			<DashboardDetails />
		</QueryClientProvider>
	);
}

async function fetchDashboardDetails(id: string, token: string) {
	const config = {
		headers: {
			Authorization: 'Bearer ' + token
		}
	};
	const { data } = await axiosInstance().get(
		`${globalEnv.BASE_URL}/dashboard/${id}/get-detailed`,
		config
	);
	return data;
}

function DashboardDetails() {
	const [dashboardId, setDashboardId] = useState('');
	const [name, setName] = useState('');
	const [userId, setUserId] = useState('');
	const [username, setUsername] = useState('');
	const [userPhoto, setUserPhoto] = useState('');
	const [dailyImage, setDailyImage] = useState('');
	const [description, setDescription] = useState('');
	const [topicsCount, setTopicsCount] = useState(0);
	const [selectedTopics, setSelectedTopics] = useState<any>([]);
	const [isDefault, setIsDefault] = useState(0);
	const [createDate, setCreateDate] = useState(null);

	const [isAddTopicModalOpen, setTopicModalOpen] = useState(false);

	const nameRef = useRef<null>(null);
	const descriptionRef = useRef<null>(null);

	const [isChanged, setIsChanged] = useState(false);

	const { id } = useParams();

	const { ACCESS_TOKEN } = useTypedSelector((state) => state.auth);

	const [dimensions, setDimensions] = useState({
		width: window.innerWidth,
		height: window.innerHeight
	});
	useEffect(() => {
		function handleResize() {
			setDimensions({
				width: window.innerWidth,
				height: window.innerHeight
			});
		}
		window.addEventListener('resize', handleResize);
		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	const dispatch = useDispatch();

	const { isError, data, isLoading, isFetching } = useQuery(
		['dashboards', dashboardId],
		() => fetchDashboardDetails(id!, ACCESS_TOKEN),
		{
			keepPreviousData: true,
			refetchOnWindowFocus: false,
			staleTime: 5000
		}
	);

	const navigate = useNavigate();

	useEffect(() => {
		if (data != null) {
			setDashboardId(data._id);
			setName(data.title);
			setIsDefault(data.isDefault);
			setDescription(data.description);
			setUserId(data.user._id);
			setUsername(data.user.username);
			setUserPhoto(data.user.photo);

			setCreateDate(data.createdAt);
			setTopicsCount(data.selectedTopics.length);
			setSelectedTopics(data.selectedTopics);

			setDailyImage(data.selectedTopics[0].dailyImage.url);
		}
	}, [data]);

	useEffect(() => {
		return () => {
			console.log('Unmount!');
		};
	}, []);

	const CreationDate = () => {
		return (
			<div>
				<div className="mb-2 text-gray-700 mt-0.5">
					<h3 className="text-sm font-bold dark:text-gray-100 text-oldgray-600 transition-colors duration-500">
						{'Creation Date: '}
					</h3>
					<h3 className="text-gray-600 dark:text-gray-400 text-sm font-bold">
						{moment(createDate).format('DD/MM/YYYY - HH:MM')}
					</h3>
				</div>
			</div>
		);
	};

	const GeneralData = () => {
		return (
			<div className="flex justify-start py-4 lg:pt-3">
				<div className="mr-4 p-3 text-center">
					<span className="text-xl font-bold block uppercase tracking-wide text-gray-800 dark:text-gray-200 transition-all duration-500">
						{topicsCount}
					</span>
					<span className="text-sm font-semibold text-gray-600 dark:text-gray-400 transition-all duration-500">
						Selected Topics
					</span>
				</div>
			</div>
		);
	};

	const saveDashboardReq = async () => {
		const dto = {
			dashboardId: dashboardId,
			name: name,
			description: description
		};
		const config = {
			headers: {
				Authorization: 'Bearer ' + ACCESS_TOKEN
			}
		};

		// FIXME: doesn't work, check backend
		const { data: response } = await axiosInstance().put(
			process.env.conf + '/admin/dashboard/update',
			dto,
			config
		);
		return response;
	};

	const deleteDashboardReq = async () => {
		const { data: response } = await axiosInstance().delete(
			globalEnv.BASE_URL + '/' + dashboardId,
			{
				headers: {
					Authorization: 'Bearer ' + ACCESS_TOKEN
				}
			}
		);
		return response;
	};

	const saveDashboard = useMutation(saveDashboardReq, {
		onSuccess: () => {
			setIsChanged(false);
			dispatch(
				SetNotificationAction({
					type: stateNotificationsEnum.SUCCESS,
					message: 'Successfully updated dashboard info.'
				})
			);
		},
		onError: () => {
			dispatch(
				SetNotificationAction({
					type: stateNotificationsEnum.ERROR,
					message: "Couldn't update dashboard info."
				})
			);
		}
	});

	const deleteDashboard = useMutation(deleteDashboardReq, {
		onSuccess: () => {
			navigate('../documents');
		},

		onError: (error) => {
			console.log(error);
			dispatch(
				SetNotificationAction({
					type: stateNotificationsEnum.ERROR,
					message: "Couldn't delete dashboard."
				})
			);
		}
	});

	return (
		<Scrollbars autoHide>
			{isLoading || isFetching ? (
				<LoadingAnimComponent />
			) : isError ? (
				<div className="h-full ml-18 ">
					<div className=" flex flex-col mx-auto my-auto text-center align-middle overflow-clip h-full ">
						<div className="mx-auto my-auto self-center">
							<div className="min-h-16">
								<DataNotFoundAnimation height={dimensions.height / 1.5} />
							</div>
							<h3 className="lg:text-2xl text-xl font-bold dark:text-gray-200 text-gray-700 -mt-8">
								{"Couldn't find the data you're looking for!"}
							</h3>
						</div>
					</div>
				</div>
			) : (
				<main className="mt-16 ml-18">
					<section className="relative block" style={{ height: '500px' }}>
						<div
							className="absolute top-0 w-full h-full bg-center bg-cover"
							style={{
								backgroundImage: `url('${dailyImage}')`
							}}
						>
							<span
								id="blackOverlay"
								className="w-full h-full absolute opacity-50 bg-black"
							></span>
						</div>
					</section>
					<section className="relative py-16 bg-gray-300 dark:bg-zinc-800 transition-all duration-500 pl-18">
						<div className="container mx-auto px-4">
							<div
								className="relative flex flex-col min-w-0 break-words bg-white dark:bg-gray-800
											 transition-all duration-500 w-full shadow-xl rounded-2xl -mt-64"
							>
								<div className="px-6">
									<div
										className="flex mx-auto bg-gray-200 dark:bg-gray-700 rounded-xl w-9/12 
												transition-all duration-500"
									>
										<div className="w-2/12 px-4">
											<GeneralData />
										</div>

										<div className="w-8/12 px-4 flex justify-center">
											<div className="flex py-4 lg:pt-3">
												<div
													className=" px-3 flex-col flex align-middle justify-center h-full cursor-pointer"
													onClick={() => navigate(`../user/${userId}`)}
												>
													<div className="absolute -top-9 self-center rounded-full flex justify-center bg-gray-300 dark:bg-gray-700 h-13 w-13 transition-colors duration-500">
														<LazyImage
															photo={
																userPhoto != null
																	? userPhoto
																	: `https://api.dicebear.com/7.x/bottts/svg?seed=${username}`
															}
															dimesnions={{ width: '70', height: '70' }}
															loaderDimesnions={70}
														/>
													</div>
													<div className="my-auto">
														<span className="text-sm text-left text-gray-800 dark:text-gray-200 font-bold transition-all duration-500">
															{username}
														</span>
													</div>
												</div>
											</div>
										</div>

										<div className="flex flex-col align-middle justify-center w-2/12 text-center">
											<div className="">
												<CreationDate />
											</div>
											<div className="">
												{isDefault ? (
													<span className="text-sm text-green-500 font-semibold transition-all duration-500">
														Default
													</span>
												) : (
													<span className="text-sm text-orange-500 font-semibold transition-all duration-500">
														Not default
													</span>
												)}
											</div>
										</div>
									</div>

									<div className="text-center">
										<div className="text-gray-800 dark:text-gray-200 font-bold mx-auto mt-3">
											<Editable
												text={name}
												placeholder="No name is specified"
												childRef={nameRef}
												type="input"
												className="text-6xl font-semibold leading-normal w-9/12 mx-auto"
												textColor="dark:text-white text-oldgray-800 transition-colors duration-500"
											>
												<input
													ref={nameRef}
													type="text"
													name="name"
													className="shadow rounded text-6xl font-semibold leading-normal text-gray-800 text-center 
													dark:bg-gray-800 transition-all duration-500 dark:text-gray-200 w-9/12"
													placeholder=""
													value={name}
													onChange={(e) => {
														setName(e.target.value);
														setIsChanged(true);
													}}
												/>
											</Editable>

											<div className="leading-normal mt-5 mb-2 text-gray-700 text-left w-9/12 mx-auto">
												<i className="fas fa-map-marker-alt mr-2 text-sm text-gray-500" />
												<label className="text-xl text-gray-600 dark:text-gray-400">
													Description:
												</label>
												<Editable
													text={description}
													placeholder="No description is provided"
													childRef={descriptionRef}
													type="textarea"
													className="font-semibold leading-normal transition-colors duration-500 w-full p-2"
													textColor="text-gray-800 dark:text-gray-200 transition-all duration-500"
												>
													<textarea
														ref={descriptionRef}
														name="name"
														className="text-gray-800 dark:text-gray-200 font-semibold leading-normal 
																	w-full p-2 dark:bg-gray-800"
														placeholder=""
														value={description}
														onChange={(e) => {
															setDescription(e.target.value);
															setIsChanged(true);
														}}
													/>
												</Editable>
											</div>
											<div className="border-t mt-8 border-gray-300 dark:border-gray-700 transition-all duration-500 text-center">
												<div className="grid grid-cols-3 gap-4 place-items-center">
													<div className=" invisible"></div>
													<h3 className="mt-2 text-3xl font-bold dark:text-gray-100">
														Selected topics
													</h3>

													{isAddTopicModalOpen && (
														<TopicAddModalForDash
															isOpen={isAddTopicModalOpen}
															setOpen={setTopicModalOpen}
															selectedTopics={selectedTopics}
															setTopics={setSelectedTopics}
															userId={userId}
															dashboardId={dashboardId}
														/>
													)}
													<button
														className="btn btn-outline btn-sm btn-primary ml-auto mt-3 mr-2 "
														onClick={() => {
															setTopicModalOpen(true);
														}}
													>
														Add ➕
													</button>
												</div>

												<div className="w-full mt-5 transition-all duration-500">
													<div className="carousel w-fit p-4 bg-gray-300 dark:bg-gray-700 rounded-box mx-auto transition-all duration-500">
														{selectedTopics.map((item: any) => (
															<TopicCard
																key={item._id}
																topicId={item._id}
																dashboardId={dashboardId}
																name={item.name}
																image={item.dailyImage}
																singleTopic={false}
																topicsList={selectedTopics}
																setTopics={setSelectedTopics}
															/>
														))}
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
								<div className="w-full px-4">
									<div className="py-6 px-3 mt-32 sm:mt-0 flex flex-row">
										<div className="justify-start flex-1">
											<button
												className="btn btn-outline btn-sm btn-error mr-1"
												type="button"
												style={{ transition: 'all .15s ease' }}
												onClick={() => deleteDashboard.mutate()}
											>
												Delete
											</button>
										</div>
										<div className="justify-end">
											<button
												className="btn btn-outline btn-sm mt-2 mr-1 "
												type="button"
												style={{ transition: 'all .15s ease' }}
												onClick={() => navigate('../documents')}
											>
												Cancel
											</button>
											{isChanged && (
												<button
													className="btn btn-outline btn-sm btn-primary mt-2 mr-1"
													type="button"
													style={{ transition: 'all .15s ease' }}
													onClick={() => saveDashboard.mutate()}
												>
													Save
												</button>
											)}
										</div>
									</div>
								</div>
							</div>
						</div>
					</section>
				</main>
			)}
		</Scrollbars>
	);
}
